import React from 'react';
import {
  Box,
  ClickAwayListener,
  FormLabel,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ChangeConnectionMenuIcon,
  DotsHorizontal,
  EditMenuIcon,
  ReconnectMenuIcon,
  TestMenuIcon,
} from 'components/SVG';
import { useDialog, usePopover } from 'hooks/common';
import EditableTitleSkeleton from 'sharedComponents/Skeleton/EditableTitleSkeleton';
import { IconButtonWrapper, InfoCard, SparkButton } from 'sharedComponents/UI';
import type { Plugins } from 'types/Plugins';
import PluginConnectionEditableField from './PluginConnectionEditableField';
import {
  usePluginConnectionByID,
  usePluginConnections,
  usePluginConnectionTest,
  useUpdatePluginConnection,
} from 'hooks/SparkBuilder/pluginFunction';
import PluginConnectionPopup from './PluginConnectionPopup';
import { useFormikContext } from 'formik';
import PluginAuthenticationModal from './PluginAuthenticationModal';
import { usePluginConnectionStatusTypes } from 'hooks/SparkBuilder/pluginFunction';
import type { PluginsFormType } from '../PluginsFormContainer';

const sortedMenuItems = [
  {
    label: 'Change Connection',
    action: 'change',
    icon: <ChangeConnectionMenuIcon />,
  },
  { label: 'Edit Name', action: 'edit', icon: <EditMenuIcon /> },
  { label: 'Test Connection', action: 'test', icon: <TestMenuIcon /> },
  { label: 'Reconnect', action: 'reconnect', icon: <ReconnectMenuIcon /> },
];

export const connectionStatusMap = {
  success: {
    type: 'success',
    message: 'Test succeeded',
  },
  error: {
    type: 'error',
    message: 'Test failed',
  },
  inActive: {
    type: 'error',
    message: 'Connection error',
  },
};

type PluginConnectionAuthenticationProps = {
  selectedPlugins: Plugins | undefined;
  pluginAuthenticationID: string | undefined;
  handleConnectionSelect: (connectionID: string) => void;
};

function PluginConnectionAuthentication({
  selectedPlugins,
  pluginAuthenticationID,
  handleConnectionSelect,
}: PluginConnectionAuthenticationProps) {
  const { setFieldValue, values: pluginFormValue } =
    useFormikContext<PluginsFormType>();
  const [isNewConnectionModal, setIsNewConnectionModal] = React.useState(false);

  const {
    open: isAuthenticationModalOpen,
    openDialog: openAuthenticationModal,
    closeDialog: closeAuthenticationModal,
  } = useDialog();

  const [isEditing, setIsEditing] = React.useState(false);

  const [showConnectionListPopUp, setShowConnectionListPopUp] =
    React.useState<boolean>(false);

  const { anchorEl, open, openPopover, closePopover } = usePopover();

  const {
    mutateAsync: testPluginConnection,
    isError: isTestPluginConnectionError,
    isSuccess: isTestPluginConnectionSuccess,
    reset: resetPluginConnectionTest,
  } = usePluginConnectionTest();

  const { pluginConnections, isLoading: isPluginConnectionLoading } =
    usePluginConnections(selectedPlugins?.id, '');

  const { mutateAsync: updatePluginConnection } = useUpdatePluginConnection();

  const defaultPluginConnectionID = pluginConnections?.at(0)?.id;

  const defaultPluginConnection = React.useMemo(() => {
    if (pluginFormValue?.pluginConnectionID) {
      return pluginConnections?.find(
        (pluginConnection) =>
          pluginConnection.id === pluginFormValue?.pluginConnectionID
      );
    } else {
      return pluginConnections?.find(
        (pluginConnection) => pluginConnection.id === defaultPluginConnectionID
      );
    }
  }, [
    defaultPluginConnectionID,
    pluginConnections,
    pluginFormValue?.pluginConnectionID,
  ]);

  const {
    pluginConnectionStatusTypeIDMap,
    isLoading: isPluginStatusTypesLoading,
  } = usePluginConnectionStatusTypes();

  //to check auth types use this
  const { pluginConnection, isLoading: isSinglePluginConnectionLoading } =
    usePluginConnectionByID(defaultPluginConnection?.id);

  const preferredPluginConnection = defaultPluginConnection || pluginConnection;

  React.useEffect(() => {
    if (defaultPluginConnectionID && !pluginFormValue?.pluginConnectionID) {
      setFieldValue('pluginConnectionID', defaultPluginConnectionID);
      handleConnectionSelect(defaultPluginConnectionID);
    }
  }, [
    defaultPluginConnectionID,
    setFieldValue,
    pluginFormValue?.pluginConnectionID,
    handleConnectionSelect,
  ]);

  const handleMenuItemClick = async (action: string) => {
    closePopover();

    switch (action) {
      case 'edit':
        setIsEditing(true);
        return;
      case 'new':
        setIsNewConnectionModal(true);
        openAuthenticationModal();
        return;
      case 'reconnect':
        setIsNewConnectionModal(false);
        openAuthenticationModal();
        return;
      case 'change':
        onShowConnectionListPopUp();
        return;
      case 'test':
        try {
          await testPluginConnection({
            pluginConnectionID: preferredPluginConnection?.id,
          });
        } catch {}
        return;
    }
  };

  const handleClose = () => {
    closePopover();
  };

  const onShowConnectionListPopUp = React.useCallback(() => {
    setShowConnectionListPopUp(true);
  }, []);

  const onCloseConnectionListPopUp = React.useCallback(() => {
    setShowConnectionListPopUp(false);
  }, []);

  const handleExpandMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    openPopover(event);
  };

  const onConnectionItemClick = (connectionID: string) => {
    setFieldValue('pluginConnectionID', connectionID);
    handleConnectionSelect(connectionID);
    resetPluginConnectionTest();
    onCloseConnectionListPopUp();
  };

  const onSaveConnectionName = (value: string) => {
    if (!preferredPluginConnection?.id) {
      return;
    }

    updatePluginConnection({
      name: value,
      pluginConnectionID: preferredPluginConnection?.id,
    });
  };
  const onCreatePluginConnection = (connectionID: string) => {
    if (connectionID) {
      setFieldValue('pluginConnectionID', connectionID);
      handleConnectionSelect(connectionID);
    }
  };

  const status = React.useMemo(() => {
    const pluginConnectionStatusType =
      pluginConnectionStatusTypeIDMap?.[
        pluginConnection?.attributes.pluginConnectionStatusTypeID + ''
      ];
    const statusType = pluginConnectionStatusType?.attributes.name;

    const inActive = statusType === 'Unauthorized';

    if (inActive && !isTestPluginConnectionError)
      return connectionStatusMap.inActive;
    if (isTestPluginConnectionError) return connectionStatusMap.error;
    if (isTestPluginConnectionSuccess && !inActive)
      return connectionStatusMap.success;
  }, [
    isTestPluginConnectionError,
    pluginConnection?.attributes.pluginConnectionStatusTypeID,
    pluginConnectionStatusTypeIDMap,
    isTestPluginConnectionSuccess,
  ]);

  const isLoading =
    isPluginStatusTypesLoading ||
    isSinglePluginConnectionLoading ||
    isPluginConnectionLoading;

  return (
    <>
      <ClickAwayListener onClickAway={onCloseConnectionListPopUp}>
        <Grid container mb={status || isLoading ? 3.5 : 0}>
          <FormLabel sx={{ fontSize: '16px' }}>* Connection</FormLabel>
          <Grid container item gap={3.5}>
            <Grid
              container
              item
              border={(theme) => `1px solid ${theme.palette.borderColor}`}
              alignItems='center'
              borderRadius='10px'
              padding={!!preferredPluginConnection ? '8px' : '12px'}
              justifyContent='space-between'
              position='relative'
            >
              {isPluginConnectionLoading ? (
                <EditableTitleSkeleton
                  boxStyles={{ width: '100%' }}
                  skeletonStyles={{ width: '100%' }}
                />
              ) : (
                <>
                  {!preferredPluginConnection ? (
                    <>
                      <Grid item xs={8}>
                        <Typography>
                          Connect {selectedPlugins?.attributes.name}
                        </Typography>
                      </Grid>
                      <Grid item maxWidth='auto'>
                        <SparkButton
                          variant='contained'
                          onClick={() => handleMenuItemClick('reconnect')}
                        >
                          Sign in
                        </SparkButton>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={isEditing ? 12 : 10.5}>
                        <PluginConnectionEditableField
                          value={preferredPluginConnection?.attributes.name}
                          onTitleSave={onSaveConnectionName}
                          setIsEditing={setIsEditing}
                          isEditing={isEditing}
                          fullWidth={true}
                        />
                      </Grid>
                      {!isEditing && (
                        <Grid
                          item
                          gap={1}
                          container
                          xs={1.5}
                          justifyContent='flex-end'
                        >
                          <IconButtonWrapper
                            onClick={handleExpandMenu}
                            sx={{
                              margin: 0,
                              width: '32px',
                              height: '32px',
                            }}
                          >
                            <DotsHorizontal />
                          </IconButtonWrapper>
                          <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                          >
                            {sortedMenuItems.map((item, index) => (
                              <MenuItem
                                key={index}
                                onClick={() => {
                                  handleMenuItemClick(item.action);
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    aspectRatio: 1,
                                    display: 'flex',
                                  }}
                                >
                                  {item.icon}
                                </Box>
                                <Typography component='span' variant='body1'>
                                  {item.label}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Menu>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              {showConnectionListPopUp && (
                <PluginConnectionPopup
                  onClose={onCloseConnectionListPopUp}
                  sx={{
                    position: 'absolute',
                    top: '110%',
                    zIndex: 500,
                    width: '100%',
                    overflow: 'auto',
                    left: 0,
                  }}
                  onConnectionItemClick={onConnectionItemClick}
                  onAddClick={() => {
                    handleMenuItemClick('new');
                  }}
                  pluginConnections={pluginConnections}
                />
              )}
            </Grid>
            <Grid item container>
              {isLoading ? (
                <EditableTitleSkeleton
                  boxStyles={{ width: '100%' }}
                  skeletonStyles={{ width: '100%', height: '45px' }}
                />
              ) : (
                !!status && (
                  <InfoCard
                    type={status?.type as any}
                    info={status?.message}
                    cardStyles={{ width: '100%' }}
                  />
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </ClickAwayListener>
      {isAuthenticationModalOpen && (
        <PluginAuthenticationModal
          isOpen={isAuthenticationModalOpen}
          onClose={closeAuthenticationModal}
          selectedPlugins={selectedPlugins}
          pluginAuthenticationID={pluginAuthenticationID}
          pluginConnection={
            isNewConnectionModal ? null : preferredPluginConnection
          }
          onCreatePluginConnection={onCreatePluginConnection}
        />
      )}
    </>
  );
}
export default PluginConnectionAuthentication;
